/**
 * The on-device cache of the coop's nest + clucks, in IndexedDB.
 *
 * The PWA does all retrieval and browsing against this cache; the network is
 * only touched to sync it (delta + batch-download). Everything stored here is
 * derived from the user's own coop files — no secrets.
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { CluckEntry, EggDraft, ExportFile, NestPage } from '../shared/coop-types.js';

const DB_NAME = 'kip-coop';
const DB_VERSION = 3;

export interface CluckMonth {
  month: string; // YYYY-MM
  entries: CluckEntry[];
  rev: string;
}

/** Sync bookkeeping — one row, key `"sync"`. */
export interface SyncMeta {
  key: 'sync';
  cursor: string | null;
  lastPulledAt: number | null;
  /** Which coop the cache holds — a change means wipe and re-pull. */
  coopPath: string | null;
}

interface CoopDB extends DBSchema {
  pages: { key: string; value: NestPage; indexes: { 'by-type': string } };
  clucks: { key: string; value: CluckMonth };
  meta: { key: string; value: SyncMeta };
  /** Captures waiting to reach Dropbox — the offline-safe write queue. */
  eggs: { key: string; value: EggDraft };
  /** Skill-generated files in exports/ — metadata only, keyed by path. */
  exports: { key: string; value: ExportFile };
}

let dbPromise: Promise<IDBPDatabase<CoopDB>> | null = null;

function db(): Promise<IDBPDatabase<CoopDB>> {
  if (!dbPromise) {
    dbPromise = openDB<CoopDB>(DB_NAME, DB_VERSION, {
      upgrade(database, oldVersion) {
        if (oldVersion < 1) {
          const pages = database.createObjectStore('pages', { keyPath: 'slug' });
          pages.createIndex('by-type', 'type');
          database.createObjectStore('clucks', { keyPath: 'month' });
          database.createObjectStore('meta', { keyPath: 'key' });
        }
        if (oldVersion < 2) {
          database.createObjectStore('eggs', { keyPath: 'clientId' });
        }
        if (oldVersion < 3) {
          database.createObjectStore('exports', { keyPath: 'path' });
        }
      },
    });
  }
  return dbPromise;
}

// --- nest pages ---------------------------------------------------------

export async function allPages(): Promise<NestPage[]> {
  return (await db()).getAll('pages');
}

export async function putPages(pages: NestPage[]): Promise<void> {
  if (!pages.length) return;
  const tx = (await db()).transaction('pages', 'readwrite');
  await Promise.all([...pages.map((p) => tx.store.put(p)), tx.done]);
}

export async function deletePages(slugs: string[]): Promise<void> {
  if (!slugs.length) return;
  const tx = (await db()).transaction('pages', 'readwrite');
  await Promise.all([...slugs.map((s) => tx.store.delete(s)), tx.done]);
}

// --- clucks ------------------------------------------------------------

export async function allClucks(): Promise<CluckMonth[]> {
  return (await db()).getAll('clucks');
}

export async function putCluck(month: CluckMonth): Promise<void> {
  await (await db()).put('clucks', month);
}

export async function deleteClucks(months: string[]): Promise<void> {
  if (!months.length) return;
  const tx = (await db()).transaction('clucks', 'readwrite');
  await Promise.all([...months.map((m) => tx.store.delete(m)), tx.done]);
}

// --- exports -----------------------------------------------------------

export async function allExports(): Promise<ExportFile[]> {
  return (await db()).getAll('exports');
}

export async function putExports(files: ExportFile[]): Promise<void> {
  if (!files.length) return;
  const tx = (await db()).transaction('exports', 'readwrite');
  await Promise.all([...files.map((f) => tx.store.put(f)), tx.done]);
}

export async function deleteExports(paths: string[]): Promise<void> {
  if (!paths.length) return;
  const tx = (await db()).transaction('exports', 'readwrite');
  await Promise.all([...paths.map((p) => tx.store.delete(p)), tx.done]);
}

// --- sync meta --------------------------------------------------------

const EMPTY_META: SyncMeta = { key: 'sync', cursor: null, lastPulledAt: null, coopPath: null };

export async function getSyncMeta(): Promise<SyncMeta> {
  return (await (await db()).get('meta', 'sync')) ?? { ...EMPTY_META };
}

export async function setSyncMeta(patch: Partial<Omit<SyncMeta, 'key'>>): Promise<SyncMeta> {
  const next: SyncMeta = { ...(await getSyncMeta()), ...patch, key: 'sync' };
  await (await db()).put('meta', next);
  return next;
}

/** Drop every cached page + cluck + export (keeps nothing) — used on a coop switch. */
export async function wipeCoop(): Promise<void> {
  const database = await db();
  const tx = database.transaction(['pages', 'clucks', 'exports'], 'readwrite');
  await Promise.all([tx.objectStore('pages').clear(), tx.objectStore('clucks').clear(), tx.objectStore('exports').clear(), tx.done]);
  await setSyncMeta({ cursor: null, lastPulledAt: null });
}

// --- capture queue ---------------------------------------------------

export async function allEggs(): Promise<EggDraft[]> {
  const eggs = await (await db()).getAll('eggs');
  return eggs.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function saveEggDraft(draft: EggDraft): Promise<void> {
  await (await db()).put('eggs', draft);
}

export async function deleteEgg(clientId: string): Promise<void> {
  await (await db()).delete('eggs', clientId);
}
